import { Formik, Form, Field } from 'formik';
import { func, object } from 'prop-types';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import cssVariables from '../../../../css/css-variables';
import { FooterNewsletterHeading, FormMessageSection, FooterNewsletter, Button } from '@lifewayit/components/dist/lib';
import { EMAIL_REGEX } from '../../utils/formUtils';
import * as Yup from 'yup';

const getInitialFormState = (data) => {
  if (data) {
    const {
      campaignId,
      formId,
      hiddenFields,
    } = data;

    const initialState = {
      firstName: '',
      lastName: '',
      emailAddress: '',
      formId,
      campaignId,
      referringUrl:
        typeof window !== 'undefined' 
          ? window.location.href.slice(0, 254)
          : '',
    };
    // reducing hidden fields to form data
    hiddenFields.reduce((accum, val) => {
      accum[val?.hiddenName] = val?.hiddenValue;
      return accum;
    }, initialState);
    return initialState;
  }
};

const NewsLetterForm = ({data, handleFormSubmit}) => {
  const [message, setMessage] = useState(null);

  const initialFormState = getInitialFormState(data);

  const validate = values => {
    const errors = {};
    if (!values.firstName) errors.firstName = 'First name is required';
    if (!values.lastName) errors.lastName = 'Last name is required';
    if (!values.emailAddress) {
      errors.emailAddress = 'Email address is required';
    } else if (!EMAIL_REGEX.test(values.emailAddress)) {
      errors.emailAddress = 'The email address is not valid';
    }

    // Show first validation error in the banner
    const firstError = Object.values(errors)[0];
    if (firstError) setMessage({ text: firstError, type: 'error' });

    return errors;
  }

  const handleOnSubmit = async (values, actions) => {
    const { setSubmitting, resetForm } = actions;
    try {
      await handleFormSubmit(values, actions);
      // Success banner
      setMessage({
        text: 'Great info coming to your inbox soon!',
        type: 'success',
      });
      resetForm();
    } catch (error) {
      setMessage({
        text: 'Something went wrong. Please try again later.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FooterNewsletter data-qa-hook="newsLetterForm">
      <Formik
        initialValues={initialFormState}
        onSubmit={handleOnSubmit}
        name={data?.formId}
        id={data?.formId}
        data-formid={data?.formId}
        data-campaignid={data?.campaignId}
        validateOnBlur={false}
        validateOnChange={false}
        validate={validate}
      >
        {({ isSubmitting, setFieldValue }) => {
          return (
          <Form disabled={isSubmitting}>
            {message ? (
              <FormMessageSection
                message={message?.text}
                type={message?.type}
              />
            ) : (
              <FooterNewsletterHeading>
                {data?.headingText}
              </FooterNewsletterHeading>
            )}
            <InputSection>
              <div>
                <StyledInput
                  data-qa-hook="firstName"
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First Name"
                  fullWidth
                  onChange={e => {
                    setMessage(null);
                    setFieldValue('firstName', e.target.value);
                  }}
                />
              </div>
              <div>
                <StyledInput
                  data-qa-hook="lastName"
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last Name"
                  fullWidth
                  onChange={e => {
                    setMessage(null);
                    setFieldValue('lastName', e.target.value);
                  }}
                />
              </div>
            </InputSection>
            <InputSection style={{ paddingTop: '10px' }}>
              <div>
                <StyledInput
                  data-qa-hook="emailAddress"
                  id="emailAddress"
                  name="emailAddress"
                  type="email"
                  placeholder="Email"
                  fullWidth
                  onChange={e => {
                    setMessage(null);
                    setFieldValue('emailAddress', e.target.value);
                  }}
                />
              </div>
            </InputSection>
          <Button
            data-qa-hook="signup-form-button"
            variant="blue"
            size="md"
            type="submit"
            className="mt-2 block rounded-full leading-relaxed text-center transition-all duration-200 solid-focus-ring button bg-blue text-white hover:text-white hover:bg-blue-hover focus:text-white focus:bg-blue-hover active:bg-blue-active px-4 pt-1 pb-1.5 text-base block rounded-full leading-relaxed text-center transition-all duration-200 solid-focus-ring"
          >
            {data?.buttonText}
          </Button>
          </Form>
        )}}
      </Formik>
    </FooterNewsletter>
  );
};

NewsLetterForm.propTypes = {
  data: object,
  handleFormSubmit: func
};

export default NewsLetterForm;

const StyledInput = styled(({ errorBorder, fullWidth, ...props }) => (
  <Field {...props} />
))`
  display: block;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cccccc;
  border-radius: 3px;
  margin-bottom: 0.5rem;
  color: #222;
  height: 35px;
  ${({ fullWidth }) => fullWidth && 'width: 100%;'}
  ${({ errorBorder }) =>
    errorBorder && `border: 1px solid ${cssVariables.colors.ErrorBright};`}
`;

const InputSection = styled.fieldset`
  display: flex;
  flex-direction: row;

  text-align: left;

  & > * {
    flex: 1 1 0;
  }

  & > *:not(:last-child) {
    margin-right: 10px;
  }
`;

const checkError = (value, touched) => (touched && !value);
